import { forwardRef } from "react";

const BASE =
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium transition-colors focus-ring disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50";

const VARIANTS = {
  primary: "bg-primary text-primary-fg hover:bg-primary-hover",
  secondary: "border border-line bg-surface text-fg hover:bg-surface-subtle",
  ghost: "text-fg hover:bg-surface-subtle",
  danger: "bg-danger text-white hover:brightness-95",
  "ghost-danger": "text-danger-fg hover:bg-danger-subtle",
};

const SIZES = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-5 text-sm",
};

const ICON_SIZES = {
  sm: "h-8 w-8 text-base",
  md: "h-10 w-10 text-lg",
  lg: "h-12 w-12 text-xl",
};

const SPINNER_SIZES = {
  sm: "h-3 w-3",
  md: "h-4 w-4",
  lg: "h-4 w-4",
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

function Spinner({ size }) {
  return (
    <span
      aria-hidden="true"
      className={`${SPINNER_SIZES[size]} inline-block animate-spin rounded-full border-2 border-current border-r-transparent`}
    />
  );
}

/**
 * Shared props for Button/IconButton when rendered as a non-button element
 * (e.g. `as={Link}`): native `disabled` does not exist there, so the disabled
 * state is expressed with `aria-disabled` and click suppression.
 */
function interactiveProps(Component, { disabled, type, onClick }) {
  if (Component === "button") {
    return { type, disabled, onClick };
  }
  return {
    "aria-disabled": disabled || undefined,
    tabIndex: disabled ? -1 : undefined,
    onClick: disabled
      ? (event) => event.preventDefault()
      : onClick,
  };
}

/**
 * Button — the single action control for the app.
 *
 * @param {object} props
 * @param {"primary"|"secondary"|"ghost"|"danger"|"ghost-danger"} [props.variant="primary"]
 *   `ghost-danger`: borderless destructive action (e.g. a row's Remove link).
 * @param {"sm"|"md"|"lg"} [props.size="md"]
 * @param {boolean} [props.loading=false] Shows a spinner, disables the control and sets aria-busy.
 * @param {boolean} [props.disabled=false]
 * @param {React.ReactNode} [props.leftIcon] Icon element rendered before the label.
 * @param {React.ReactNode} [props.rightIcon] Icon element rendered after the label.
 * @param {boolean} [props.fullWidth=false] Stretches to the container width.
 * @param {React.ElementType} [props.as="button"] Element/component to render, e.g. react-router `Link` (pass `to`).
 * @param {"button"|"submit"|"reset"} [props.type="button"] Only applied when rendering a `<button>`.
 * @param {string} [props.className]
 */
const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    as: Component = "button",
    type = "button",
    className,
    onClick,
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  return (
    <Component
      ref={ref}
      className={cx(
        BASE,
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        fullWidth && "w-full",
        className
      )}
      aria-busy={loading || undefined}
      {...interactiveProps(Component, { disabled: isDisabled, type, onClick })}
      {...rest}
    >
      {loading ? <Spinner size={SIZES[size] ? size : "md"} /> : leftIcon}
      {children}
      {rightIcon}
    </Component>
  );
});

/**
 * IconButton — square icon-only button; `label` is required and becomes the
 * accessible name (`aria-label`) and hover hint (`title`).
 *
 * @param {object} props
 * @param {React.ReactNode} props.icon Icon element, e.g. `<MdClose />`.
 * @param {string} props.label Accessible name (required).
 * @param {"primary"|"secondary"|"ghost"|"danger"|"ghost-danger"} [props.variant="ghost"]
 * @param {"sm"|"md"|"lg"} [props.size="md"] sm 32px / md 40px / lg 48px square.
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.disabled=false]
 * @param {React.ElementType} [props.as="button"]
 * @param {"button"|"submit"|"reset"} [props.type="button"]
 * @param {string} [props.className]
 */
export const IconButton = forwardRef(function IconButton(
  {
    icon,
    label,
    variant = "ghost",
    size = "md",
    loading = false,
    disabled = false,
    as: Component = "button",
    type = "button",
    className,
    onClick,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  return (
    <Component
      ref={ref}
      aria-label={label}
      title={label}
      className={cx(
        BASE,
        VARIANTS[variant] || VARIANTS.ghost,
        ICON_SIZES[size] || ICON_SIZES.md,
        className
      )}
      aria-busy={loading || undefined}
      {...interactiveProps(Component, { disabled: isDisabled, type, onClick })}
      {...rest}
    >
      {loading ? <Spinner size={ICON_SIZES[size] ? size : "md"} /> : icon}
    </Component>
  );
});

export default Button;
